/**
 * TeamPage — admin-only team management. Lists members + pending invitations,
 * with inline forms to invite, revoke, and remove. Non-admins are bounced to
 * the dashboard with a friendly empty state.
 */
import { useCallback, useEffect, useState } from 'react';
import { TopBar } from '../layout/TopBar.jsx';
import { ScrollX } from '../components/ScrollX.jsx';
import { api, ApiError } from '../api/client.js';
import { formatDateDDMonYYYY } from '../../utils/dates.js';

export function TeamPage({ user, organization, onLogout, navigate }) {
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteStatus, setInviteStatus] = useState('idle'); // idle | sending | error | sent
  const [inviteError, setInviteError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const isAdmin = user?.role === 'admin';

  const loadAll = useCallback(async () => {
    try {
      const [m, i] = await Promise.all([api.listMembers(), api.listInvitations()]);
      setMembers(Array.isArray(m?.members) ? m.members : []);
      setInvitations(Array.isArray(i?.invitations) ? i.invitations : []);
      setLoadError(null);
    } catch (e) {
      setLoadError(e);
    }
  }, []);

  useEffect(() => { if (isAdmin) loadAll(); }, [isAdmin, loadAll]);

  if (!isAdmin) {
    return (
      <div className="app-frame">
        <TopBar
          user={user}
          organization={organization}
          onLogout={onLogout}
          navigate={navigate}
        />
        <main className="main-content" role="main">
          <section className="team-no-access">
            <h1>Team access</h1>
            <p>Only organisation admins can view team management.</p>
            <button type="button" className="btn-submit" onClick={() => navigate('/dashboard')}>
              Back to dashboard
            </button>
          </section>
        </main>
      </div>
    );
  }

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteStatus('sending');
    setInviteError(null);
    try {
      await api.createInvitation({ email: inviteEmail.trim() });
      setInviteEmail('');
      setInviteStatus('sent');
      await loadAll();
      setTimeout(() => setInviteStatus('idle'), 3000);
    } catch (err) {
      setInviteStatus('error');
      setInviteError(err);
    }
  };

  const handleRevoke = async (id) => {
    setActionError(null);
    try {
      await api.revokeInvitation(id);
      await loadAll();
    } catch (err) {
      setActionError(err);
    }
  };

  const handleRemove = async (id, fullName) => {
    setActionError(null);
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${fullName || 'this member'} from ${organization?.name || 'the team'}?`)) {
      return;
    }
    try {
      await api.removeMember(id);
      await loadAll();
    } catch (err) {
      setActionError(err);
    }
  };

  return (
    <div className="app-frame">
      <TopBar
        user={user}
        organization={organization}
        onLogout={onLogout}
        navigate={navigate}
      />
      <main className="main-content team-page" role="main">
        <section className="team-hero">
          <h1>Team &middot; {organization?.name || '—'}</h1>
          <p>Invite teammates, manage members, and track pending invitations.</p>
        </section>

        {loadError && (
          <p className="recent-reports-error" role="alert">
            Couldn&rsquo;t load team data. Try refreshing.
          </p>
        )}

        <section className="invite-form-card">
          <h2>Invite a teammate</h2>
          <form onSubmit={handleInvite} className="invite-form">
            <label htmlFor="invite-email" className="sr-only">Invitee email</label>
            <input
              id="invite-email"
              type="email"
              className="text-input"
              placeholder="name@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
              autoComplete="email"
            />
            <button
              type="submit"
              className="btn-submit"
              disabled={inviteStatus === 'sending' || !inviteEmail.trim()}
            >
              {inviteStatus === 'sending' ? 'Sending…' : 'Send invitation'}
            </button>
          </form>
          {inviteStatus === 'sent' && (
            <p className="invite-success" role="status">Invitation sent.</p>
          )}
          {inviteStatus === 'error' && inviteError && (
            <p className="invite-error" role="alert">
              {inviteError instanceof ApiError
                ? (inviteError.message || 'Couldn\u2019t send invitation.')
                : 'Couldn\u2019t send invitation.'}
            </p>
          )}
        </section>

        {actionError && (
          <p className="invite-error" role="alert">
            {actionError instanceof ApiError
              ? (actionError.message || 'Action failed.')
              : 'Action failed.'}
          </p>
        )}

        <section className="team-members-section">
          <h2>Members ({members.length})</h2>
          {/* MOBILE-FIT M2 residuals round (v3.34): the members table slides —
              the harness caught "Last sign-in" off-screen at 360. */}
          <ScrollX className="responsive-table">
            <table className="team-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Last sign-in</th>
                  <th aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id}>
                    <td>{m.full_name}</td>
                    <td>{m.email}</td>
                    <td>
                      <span className={`role-chip role-${m.role}`}>{m.role}</span>
                    </td>
                    <td>{formatDate(m.last_login_at)}</td>
                    <td>
                      {m.role !== 'admin' && m.id !== user?.id && (
                        <button
                          type="button"
                          className="btn-link-danger"
                          onClick={() => handleRemove(m.id, m.full_name)}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        </section>

        <section className="team-invitations-section">
          <h2>Pending invitations ({invitations.length})</h2>
          {invitations.length === 0 ? (
            <p className="recent-reports-empty">No pending invitations.</p>
          ) : (
            <ScrollX className="responsive-table">
              <table className="team-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Invited by</th>
                    <th>Sent</th>
                    <th>Expires</th>
                    <th aria-label="Actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((inv) => (
                    <tr key={inv.id}>
                      <td>{inv.invited_email}</td>
                      <td>{inv.invited_by_name}</td>
                      <td>{formatDate(inv.created_at)}</td>
                      <td>{formatDate(inv.expires_at)}</td>
                      <td>
                        <button
                          type="button"
                          className="btn-link-danger"
                          onClick={() => handleRevoke(inv.id)}
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          )}
        </section>
      </main>
    </div>
  );
}

// A1 (D-ListDateFormat): route through the one shared DD Mon YYYY formatter.
function formatDate(iso) {
  return formatDateDDMonYYYY(iso);
}
